import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, PlayCircle, Stop, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

const STATUS = {
  running: { icon: <CircleNotch size={13} className="animate-spin text-sky-500" />, label: "Probando…" },
  match: { icon: <CheckCircle size={13} weight="fill" className="text-emerald-600" />, label: "Coincide" },
  diff: { icon: <XCircle size={13} weight="fill" className="text-red-600" />, label: "Distinto" },
  error: { icon: <XCircle size={13} className="text-muted-foreground" />, label: "Sin comparar" },
};

function Row({ ex, id, onTry, res }) {
  const st = res && STATUS[res.status];
  return (
    <div className={`px-4 py-2 text-xs ${res?.status === "diff" ? "bg-red-500/5" : ""}`} data-testid={`bt-example-${id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{ex.kind}</div>
          <div className="font-medium">«{ex.cliente}»</div>
        </div>
        {st && <span className="flex items-center gap-1 shrink-0 text-[11px]" data-testid={`bt-example-status-${id}`}>{st.icon}{st.label}</span>}
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs shrink-0" onClick={() => onTry(ex)} data-testid={`bt-example-try-${id}`}>
          <Play size={11} /> Probar
        </Button>
      </div>
      {res?.status === "diff" && (
        <div className="mt-1.5 space-y-1 text-[11px]" data-testid={`bt-example-diff-${id}`}>
          <div className="text-red-700 dark:text-red-400">{res.reason}</div>
          <div><b>Respondió:</b> {res.reply}</div>
          <div className="text-muted-foreground"><b>Esperado:</b> {ex.bot}</div>
        </div>
      )}
    </div>
  );
}

function useRunAll(all) {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const stop = useRef(false);
  const run = async () => {
    stop.current = false; setRunning(true); setResults({});
    for (const [id, ex] of all) {
      if (stop.current) break;
      setResults((r) => ({ ...r, [id]: { status: "running" } }));
      try {
        const { data } = await api.post("/bot-training/check-example", { cliente: ex.cliente, bot: ex.bot });
        const status = data.match === true ? "match" : data.match === false ? "diff" : "error";
        setResults((r) => ({ ...r, [id]: { status, reply: data.reply, reason: data.reason } }));
      } catch {
        setResults((r) => ({ ...r, [id]: { status: "error" } }));
      }
    }
    setRunning(false);
  };
  return { results, running, run, stopRun: () => { stop.current = true; } };
}

export const RuleExamples = ({ onTry }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/bot-training/examples").then(({ data: d }) => setData(d)).catch(() => setData({ live: [], base: [] })); }, []);
  const all = data ? [...data.live.map((e, i) => [`live-${i}`, e]), ...data.base.map((e, i) => [`base-${i}`, e])] : [];
  const { results, running, run, stopRun } = useRunAll(all);
  if (!data) return null;
  const done = Object.values(results).filter((r) => r.status !== "running");
  const diffs = done.filter((r) => r.status === "diff").length;
  const group = (title, list, prefix) => (
    <>
      <div className="px-4 py-1.5 bg-secondary/60 text-[11px] font-semibold">{title}</div>
      {list.length === 0 && <div className="px-4 py-2 text-xs text-muted-foreground">Sin ejemplos todavía.</div>}
      {list.map((ex, i) => <Row key={`${prefix}${i}`} ex={ex} id={`${prefix}${i}`} onTry={onTry} res={results[`${prefix}${i}`]} />)}
    </>
  );
  return (
    <section className="border border-border rounded-lg bg-card" data-testid="bt-examples">
      <div className="px-4 py-2.5 border-b border-border flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Ejemplos del historial</div>
          <div className="text-[11px] text-muted-foreground">Probar envía el mensaje al chat de prueba (usa el borrador guardado). Probar todo los corre seguidos y marca los que responden distinto.</div>
          {done.length > 0 && (
            <div className="text-[11px] mt-1 font-medium" data-testid="bt-examples-summary">
              {done.length} de {all.length} probados · {done.length - diffs} coinciden · <span className={diffs ? "text-red-600" : ""}>{diffs} distintos</span>
            </div>
          )}
        </div>
        {running ? (
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs shrink-0" onClick={stopRun} data-testid="bt-examples-stop"><Stop size={12} /> Detener</Button>
        ) : (
          <Button size="sm" className="h-7 gap-1 text-xs shrink-0" disabled={!all.length} onClick={run} data-testid="bt-examples-run-all"><PlayCircle size={13} /> Probar todo</Button>
        )}
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {group(data.version ? `Tus reglas en vivo · versión ${data.version}` : "Tus reglas en vivo", data.live, "live-")}
        {group("Reglas base", data.base, "base-")}
      </div>
    </section>
  );
};

export default RuleExamples;
